import { ChangeEvent, ReactElement, useEffect, useState } from 'react';
import i18next from 'i18next';
import { useTenantSettings, useUpdateTenantSettings } from '../../../hooks/useTenantSettings';

const MIN_TRAINING_COUNT: number = 12;
const MAX_TRAINING_COUNT: number = 20;

const TenantSettings = (): ReactElement => {
  const { data, isLoading, isError } = useTenantSettings();
  const updateTenantSettingsMutation = useUpdateTenantSettings();

  const [isVideoFeedbackEnabled, setIsVideoFeedbackEnabled] = useState(false);
  const [maxTrainingCount, setMaxTrainingCount] = useState('');
  const [udiWeb, setUdiWeb] = useState('');
  const [udiAndroid, setUdiAndroid] = useState('');
  const [udiIos, setUdiIos] = useState('');

  useEffect(() => {
    if (!data) {
      return;
    }

    setIsVideoFeedbackEnabled(data.isVideoFeedbackEnabled);
    setMaxTrainingCount(String(data.maxTrainingCount));
    setUdiWeb(data.udiDi?.web ?? '');
    setUdiAndroid(data.udiDi?.android ?? '');
    setUdiIos(data.udiDi?.ios ?? '');
  }, [data]);

  const parsedMaxTrainingCount: number = Number(maxTrainingCount);

  const isMaxTrainingCountInvalid: boolean =
    maxTrainingCount.trim() === '' ||
    !Number.isInteger(parsedMaxTrainingCount) ||
    parsedMaxTrainingCount < MIN_TRAINING_COUNT ||
    parsedMaxTrainingCount > MAX_TRAINING_COUNT;

  const hasChanges: boolean = Boolean(
    data &&
    (data.isVideoFeedbackEnabled !== isVideoFeedbackEnabled ||
      data.maxTrainingCount !== parsedMaxTrainingCount ||
      (data.udiDi?.web ?? '') !== udiWeb.trim() ||
      (data.udiDi?.android ?? '') !== udiAndroid.trim() ||
      (data.udiDi?.ios ?? '') !== udiIos.trim()),
  );

  const isSaving: boolean = updateTenantSettingsMutation.isPending;
  const canSave: boolean = hasChanges && !isSaving && !isMaxTrainingCountInvalid;
  const showSuccessMessage: boolean = updateTenantSettingsMutation.isSuccess && !hasChanges;

  function handleVideoFeedbackChange(event: ChangeEvent<HTMLInputElement>): void {
    setIsVideoFeedbackEnabled(event.target.checked);
  }

  function handleMaxTrainingCountChange(event: ChangeEvent<HTMLInputElement>): void {
    setMaxTrainingCount(event.target.value);
  }

  async function handleSubmit(): Promise<void> {
    if (!canSave) {
      return;
    }

    await updateTenantSettingsMutation.mutateAsync({
      isVideoFeedbackEnabled,
      maxTrainingCount: parsedMaxTrainingCount,
      udiDi: {
        web: udiWeb.trim() || undefined,
        android: udiAndroid.trim() || undefined,
        ios: udiIos.trim() || undefined,
      },
    });
  }

  if (isLoading) {
    return (
      <div className="container-fluid py-4">
        <div className="card border-0 shadow-sm">
          <div className="card-body d-flex align-items-center gap-3">
            <span
              className="spinner-border spinner-border-sm"
              aria-hidden="true"
            />
            <span>{i18next.t('superAdminTenantSettings.loading')}</span>
          </div>
        </div>
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="container-fluid py-4">
        <div
          className="alert alert-danger mb-0"
          role="alert">
          {i18next.t('superAdminTenantSettings.error')}
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-4">
      <div className="mb-4">
        <h1 className="h3 mb-1">{i18next.t('superAdminTenantSettings.title')}</h1>
        <div className="text-muted">
          {i18next.t('superAdminTenantSettings.description', {
            defaultValue: 'Einstellungen für diesen Mandanten verwalten.',
          })}
        </div>
      </div>

      <div className="d-flex flex-column gap-3">
        <div className="card border-0 shadow-sm">
          <div className="card-body p-4">
            <div className="d-flex align-items-center justify-content-between gap-4">
              <div>
                <h2 className="h5 mb-1">
                  {i18next.t('superAdminTenantSettings.videoFeedbackTitle', {
                    defaultValue: 'Video-Feedback',
                  })}
                </h2>

                <div className="text-muted small">
                  {i18next.t('superAdminTenantSettings.videoFeedbackDescription', {
                    defaultValue: 'Feedback nach Video-Sessions erlauben.',
                  })}
                </div>
              </div>

              <div className="form-check form-switch mb-0">
                <input
                  id="isVideoFeedbackEnabled"
                  type="checkbox"
                  className="form-check-input"
                  role="switch"
                  checked={isVideoFeedbackEnabled}
                  onChange={handleVideoFeedbackChange}
                />

                <label
                  className="visually-hidden"
                  htmlFor="isVideoFeedbackEnabled">
                  {i18next.t('superAdminTenantSettings.videoFeedbackLabel')}
                </label>
              </div>
            </div>
          </div>
        </div>

        <div className="card border-0 shadow-sm">
          <div className="card-body p-4">
            <div className="row align-items-center g-3">
              <div className="col-12 col-md">
                <h2 className="h5 mb-1">
                  {i18next.t('superAdminTenantSettings.maxTrainingCountTitle')}
                </h2>

                <div className="text-muted small">
                  {i18next.t('superAdminTenantSettings.maxTrainingCountDescription')}
                </div>
              </div>

              <div className="col-12 col-md-3 col-lg-2">
                <label
                  className="visually-hidden"
                  htmlFor="maxTrainingCount">
                  {i18next.t('superAdminTenantSettings.maxTrainingCountLabel')}
                </label>

                <input
                  id="maxTrainingCount"
                  type="number"
                  className={`form-control ${isMaxTrainingCountInvalid ? 'is-invalid' : ''}`}
                  min={MIN_TRAINING_COUNT}
                  max={MAX_TRAINING_COUNT}
                  step={1}
                  value={maxTrainingCount}
                  onChange={handleMaxTrainingCountChange}
                />
              </div>
            </div>

            {isMaxTrainingCountInvalid && (
              <div className="text-danger small mt-2">
                {i18next.t('superAdminTenantSettings.maxTrainingCountInvalid', {
                  defaultValue: 'Bitte eine Zahl zwischen {{min}} und {{max}} eingeben.',
                  min: MIN_TRAINING_COUNT,
                  max: MAX_TRAINING_COUNT,
                })}
              </div>
            )}
          </div>
        </div>

        <div className="card border-0 shadow-sm">
          <div className="card-body p-4">
            <h2 className="h5 mb-1">
              {i18next.t('superAdminTenantSettings.udiDiTitle')}
            </h2>

            <div className="text-muted small mb-3">
              {i18next.t('superAdminTenantSettings.udiDiDescription')}
            </div>

            <div className="mb-3">
              <label
                className="form-label"
                htmlFor="udiWeb">
                {i18next.t('superAdminTenantSettings.udiWebLabel')}
              </label>

              <input
                id="udiWeb"
                type="text"
                className="form-control"
                value={udiWeb}
                onChange={(event: ChangeEvent<HTMLInputElement>) => setUdiWeb(event.target.value)}
              />
            </div>

            <div className="mb-3">
              <label
                className="form-label"
                htmlFor="udiAndroid">
                {i18next.t('superAdminTenantSettings.udiAndroidLabel')}
              </label>

              <input
                id="udiAndroid"
                type="text"
                className="form-control"
                value={udiAndroid}
                onChange={(event: ChangeEvent<HTMLInputElement>) => setUdiAndroid(event.target.value)}
              />
            </div>

            <div className="mb-0">
              <label
                className="form-label"
                htmlFor="udiIos">
                {i18next.t('superAdminTenantSettings.udiIosLabel')}
              </label>

              <input
                id="udiIos"
                type="text"
                className="form-control"
                value={udiIos}
                onChange={(event: ChangeEvent<HTMLInputElement>) => setUdiIos(event.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="card border-0 shadow-sm">
          <div className="card-body p-4">
            <div className="d-flex align-items-center justify-content-between gap-3 flex-wrap">
              <div className="small text-muted">
                {hasChanges
                  ? i18next.t('generic.unsavedChanges', {
                    defaultValue: 'Ungespeicherte Änderungen',
                  })
                  : i18next.t('generic.noUnsavedChanges', {
                    defaultValue: 'Keine Änderungen',
                  })}
              </div>

              <button
                type="button"
                className="btn btn-primary d-inline-flex align-items-center gap-2"
                disabled={!canSave}
                onClick={() => {
                  void handleSubmit();
                }}>
                {isSaving && (
                  <span
                    className="spinner-border spinner-border-sm"
                    aria-hidden="true"
                  />
                )}

                {isSaving ? i18next.t('generic.saving') : i18next.t('generic.save')}
              </button>
            </div>

            {showSuccessMessage && (
              <div
                className="alert alert-success mt-3 mb-0"
                role="alert">
                {i18next.t('superAdminTenantSettings.success')}
              </div>
            )}

            {updateTenantSettingsMutation.isError && (
              <div
                className="alert alert-danger mt-3 mb-0"
                role="alert">
                {i18next.t('superAdminTenantSettings.saveError')}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TenantSettings;